/**
 * Shared DEV_ACTOR_MAP for mock auth mode.
 *
 * DRR-201: All roles MUST match role-registry.yaml IDs.
 * Run `ferp-seed validate-auth-chain --check-dev-parity` to verify.
 *
 * This file is dev-only — production resolves actors via Authentik + role configs.
 */

export interface DevActor {
  id: string;
  name: string;
  email: string;
  role: string;
}

/**
 * Dev actor personas keyed by auth ID.
 * Used by AuthContext for mock auth and by ceremony defaults for role resolution.
 *
 * Role IDs must match data/configs/role-registry.yaml
 */
export const DEV_ACTOR_MAP: Record<string, { name: string; email: string; role: string }> = {
  // ── FERP System (tenant 0) ─────────────────────────────────────────────────
  '0_1':                    { name: 'System Admin',           email: 'admin@ferp.system',            role: 'admin' },

  // ── FERP Operations (tenant 1) ─────────────────────────────────────────────
  '1_1':                    { name: 'Operations Admin',       email: 'admin@ferp.operations',        role: 'admin' },

  // ── Hillside Harmony (ALF / 144G, tenant 2) ────────────────────────────────
  // MUST match data/tenants/hillside-harmony/actors.yaml (DRR-303)
  // DRR-330: Role IDs from data/configs/roles/core-*.yaml (legislation-derived)
  // DRR-220: Sequential actor IDs matching actors.yaml (2_N format)
  '2_1':                    { name: 'Admin User',             email: 'admin@hillsideharmony.com',    role: 'core-011' },  // ALD (144G.50)
  '2_2':                    { name: 'Bao Vang',               email: 'don@hillsideharmony.com',      role: 'core-012' },  // DoN (144G.51)
  '2_3':                    { name: 'Der Vang',               email: 'der@hillsideharmony.com',      role: 'core-017' },  // ULP-delegated (144G.71 Subd 6-7)
  '2_4':                    { name: 'Xee Vang',               email: 'xee@hillsideharmony.com',      role: 'core-017' },  // ULP-delegated
  '2_5':                    { name: 'Chanhsuk Vang',          email: 'chanhsuk@hillsideharmony.com', role: 'core-017' },  // ULP-delegated
  '2_6':                    { name: 'Manee Xiong',            email: 'manee@hillsideharmony.com',    role: 'core-017' },  // ULP-delegated
  '2_7':                    { name: 'Pai Yang',               email: 'pai@hillsideharmony.com',      role: 'core-017' },  // ULP-delegated
  '2_8':                    { name: 'Xhu Vang',               email: 'xhu@hillsideharmony.com',      role: 'core-017' },  // ULP-delegated
  '2_9':                    { name: 'Pany Thao',              email: 'pany@hillsideharmony.com',     role: 'core-017' },  // ULP-delegated
  '2_10':                   { name: 'Chelsea Thao',           email: 'chelsea@hillsideharmony.com',  role: 'core-018' },  // Med Aide (144G.71)
  '2_11':                   { name: 'Bee Yang',               email: 'bee@hillsideharmony.com',      role: 'core-016' },  // ULP-basic (144G.08)
  '2_12':                   { name: 'Bao Vang',               email: 'bao2@hillsideharmony.com',     role: 'core-016' },  // ULP-basic
  '2_50':                   { name: 'Phineas Vang',           email: 'ceo@hillsideharmony.com',      role: 'ceo-owner' }, // CEO/Owner (DRR-298)
  '2_99':                   { name: 'Phineas Vang (Architect)', email: 'architect@hillsideharmony.com', role: 'architect' }, // System Architect
  // ── Hillside Harmony — Composite Actors (not for impersonation) ────────────
  // Note: 2_20, 2_21 are org/facility composites per actors.yaml

  // ── Athanor Assisted Living, LLC (ALF / 144G, tenant 3) ────────────────────
  '3_1':                    { name: 'Athanor Admin',          email: 'admin@athanor-alf.com',        role: 'core-011' },  // ALD
  '3_2':                    { name: 'Athanor DoN',            email: 'don@athanor-alf.com',          role: 'core-012' },  // DoN

  // ── Omniquark, LLC (Professional Services, tenant 4) ───────────────────────
  '4_1':                    { name: 'Phin Vang',              email: 'phin@omniquark.com',           role: 'role-0-0-2' },
  '4_2':                    { name: 'Developer',              email: 'dev@omniquark.com',            role: 'role-0-0-1' },

  // ── Culturally Conscious Kitchen (Food Service, tenant 5) ──────────────────
  '5_1':                    { name: 'Kitchen Admin',          email: 'admin@cc-kitchen.com',         role: 'role-0-0-2' },
  '5_2':                    { name: 'Kitchen Manager',        email: 'manager@cc-kitchen.com',       role: 'operational-qa' },

  // ── Culturally Conscious Laundry (Laundry Service, tenant 6) ───────────────
  '6_1':                    { name: 'Laundry Admin',          email: 'admin@cc-laundry.com',         role: 'role-0-0-2' },
  '6_2':                    { name: 'Laundry Manager',        email: 'manager@cc-laundry.com',       role: 'operational-qa' },

  // ── AAHHCS (Home Care / CFSS, tenant 7) ────────────────────────────────────
  '7_1':                    { name: 'Phin Vang',              email: 'phin@aahhcs.com',              role: 'role-0-0-2' },
  '7_2':                    { name: 'Mai Lee',                email: 'mai@aahhcs.com',               role: 'care-coordinator' },
  '7_3':                    { name: 'Fatima Hassan',          email: 'fatima@aahhcs.com',            role: 'pca-home-care' },
  '7_4':                    { name: 'Sarah Johnson',          email: 'sarah@aahhcs.com',             role: 'financial-analyst' },
  '7_5':                    { name: 'David Chen',             email: 'david@aahhcs.com',             role: 'operational-qa' },

  // ── Athanor Beauty & Wellness (tenant 21) ──────────────────────────────────
  '21_1':                   { name: 'Wellness Admin',         email: 'admin@athanor-wellness.com',   role: 'role-0-0-2' },

  // ── Athanor Celebration of Life (tenant 22) ────────────────────────────────
  '22_1':                   { name: 'Memorial Admin',         email: 'admin@athanor-memorial.com',   role: 'role-0-0-2' },

  // ── Phin Personal Finance (tenant 23) ──────────────────────────────────────
  '23_1':                   { name: 'Phin Vang',              email: 'phin@personal.ferp.local',     role: 'role-0-0-2' },
};

/**
 * Ceremony signing role → compatible DEV_ACTOR_MAP roles.
 * Derived from wf-4-0-109 (care plan signing ceremony) + MN 144G requirements.
 *
 * DRR-201: All role IDs must match role-registry.yaml
 */
const CEREMONY_ROLE_TO_ACTOR_ROLES: Record<string, string[]> = {
  // DRR-330: Role IDs match data/configs/roles/core-*.yaml (legislation-derived)
  resident_or_representative: ['core-024', 'core-023', 'core-027'],  // Resident, Family, Responsible Party
  designated_coordinator: ['core-021', 'core-012'],  // QP, DoN
  licensed_nurse: ['core-013', 'core-012', 'core-014'],  // RN, DoN, LPN
  witness: ['core-020', 'role-0-0-1'],  // QA, admin
  approver: ['core-011', 'architect', 'core-012'],  // ALD, architect, DoN
  author: ['core-011', 'architect', 'core-021', 'core-014', 'core-020', 'core-019'],  // ALD, architect, QP, LPN, QA, billing
};

/**
 * Get all dev actors whose role is compatible with a ceremony signing role.
 */
export function getActorsForCeremonyRole(ceremonyRole: string): DevActor[] {
  const compatibleRoles = CEREMONY_ROLE_TO_ACTOR_ROLES[ceremonyRole] ?? [];
  if (compatibleRoles.length === 0) return [];

  return Object.entries(DEV_ACTOR_MAP)
    .filter(([, actor]) => compatibleRoles.includes(actor.role))
    .map(([id, actor]) => ({ id, ...actor }));
}

/**
 * Get all dev actors as a flat list.
 */
export function getAllDevActors(): DevActor[] {
  return Object.entries(DEV_ACTOR_MAP).map(([id, actor]) => ({ id, ...actor }));
}
